import PropTypes from "prop-types";
import React, {useEffect, useMemo, useState} from "react";
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import SoftButton from "../../../components/SoftButton";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftInput from "../../../components/SoftInput";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import {validateAge, validateImageFile, validateName} from "../../../utils/formValidations";
import {CHILD_STATUS_SELECT_OPTIONS, GENDER_SELECT_OPTIONS} from "../../../utils/formConstants";
import {handleApiError, showCreateSuccess, showFormErrors, showUpdateSuccess} from "../../../utils/notificationUtils";
import {getAllBranches} from "../../../api/branch/branchApiClient";
import {getAllRoomsByBranchId } from '../../../api/room/roomApiClient';

function AddChildrenDialog({
                               open,
                               onClose,
                               onSubmit,
                               child,
                               isEdit,
                               guardian,
                               preSelectedGuardian,
                               disableGuardianSelection = false,
                               fromGuardianView = false,
                               onUpdate = null
                           }) {
    const initialState = useMemo(() => ({
        branchId: preSelectedGuardian?.branchId || preSelectedGuardian?.branch?.id || guardian?.branchId || guardian?.branch?.id,
        roomId: null,
        childId: 0,
        firstName: "",
        lastName: "",
        gender: "",
        status: "ACTIVE",
        profilePic: null,
        remarks: "",
        dob: "",
        additionalImages: [],
    }), [guardian?.branchId, guardian?.branch?.id, preSelectedGuardian?.branchId, preSelectedGuardian?.branch?.id]);
    const [formData, setFormData] = useState(initialState);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewImages, setPreviewImages] = useState([]);
    const [deletedExistingImages, setDeletedExistingImages] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [branchesError, setBranchesError] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [loadingRooms, setLoadingRooms] = useState(false);
    const [roomsError, setRoomsError] = useState(null);

    function base64ToFile(base64String, filename, mimeType) {
        const arr = base64String.split(',');
        const mime = mimeType || arr[0].match(/:(.*?);/)[1]; // e.g. "image/png"
        const bstr = atob(arr[1]); // decode base64
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, {type: mime});
    }

    useEffect(() => {
        let isMounted = true;

        if (open) {
            // Load branches for both add and edit modes
            setLoadingBranches(true);
            setBranchesError(null);
            getAllBranches()
                .then(response => {
                    if (isMounted) {
                        const branchesData = Array.isArray(response) ? response : (response?.data || []);
                        setBranches(branchesData);
                        setBranchesError(null);
                    }
                })
                .catch(error => {
                    if (isMounted) {
                        console.error('Error loading branches:', error);
                        setBranchesError(error.message || 'Failed to load branches');
                        setBranches([]);
                    }
                })
                .finally(() => {
                    if (isMounted) {
                        setLoadingBranches(false);
                    }
                });

                // Also load rooms when branch is selected or dialog opens
                const branchId = formData.branchId || guardian?.branchId || guardian?.branch?.id;
                if (branchId) {
                    loadRooms(branchId);
                }
        }

        return () => {
            isMounted = false;
        };
    }, [open, guardian, isEdit, formData.branchId]);

    useEffect(() => {
        if (isEdit && child) {
            console.log('=== AddChildrenDialog Edit Mode ===');
            console.log('Child prop received:', child);
            console.log('Child type:', typeof child);
            console.log('Child keys:', Object.keys(child || {}));
            console.log('Child images property:', child.images);
            console.log('Child images type:', typeof child.images);
            console.log('Child images length:', child.images?.length);
            console.log('Guardian ID for edit:', guardian?.id);

            if (child.images && Array.isArray(child.images)) {
                console.log('First image structure:', child.images[0]);
                console.log('First image keys:', Object.keys(child.images[0] || {}));
            }

            console.log('Setting form data for edit mode');
            
            setFormData({
                branchId: child.branchId || guardian?.branchId || guardian?.branch?.id,
                roomId: child.roomId || null,
                childId: child.childId,
                firstName: child.firstName,
                lastName: child.lastName,
                gender: child.gender || "",
                status: child.status || "",
                profilePic: child.profilePic ? base64ToFile(`data:image/png;base64,${child.profilePic}`, 'profiler', "image/png") : null,
                remarks: child.remarks,
                dob: new Date(child.dob).toISOString().split("T")[0],
                additionalImages: child.images || [],
            });
            if (child.profilePic) {
                setPreviewUrl(`data:image/png;base64,${child.profilePic}`);
            } else {
                setPreviewUrl(null);
            }

            // Set existing images for preview only
            if (child.images && child.images.length > 0) {
                console.log('Setting existing images for preview:', child.images);
                const existingPreviews = child.images.map((image, index) => {
                    console.log(`Processing image ${index}:`, image);
                    return {
                        id: `existing-${image.imageId || index}-${Date.now()}`,
                        url: image.publicImageUrl || image.imageUrl || `data:image/png;base64,${image.image}`,
                        file: null,
                        isExisting: true,
                        originalImage: image
                    };
                });
                console.log('Created existing previews:', existingPreviews);
                setPreviewImages(existingPreviews);
                // Don't add existing images to form data - they stay in database
            } else {
                console.log('No existing images found');
                setPreviewImages([]);
            }
        } else if (open) {
            setFormData(initialState);
            setPreviewUrl(null);
            setPreviewImages([]);
            setDeletedExistingImages([]);
        }
    }, [isEdit, child, open, guardian?.id]);

    const handleChange = (e) => {
        const {name, value} = e.target;

        setFormData(prev => {
            const newFormData = {
                ...prev,
                [name]: value
            };


            return newFormData;
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (validateImage(e)) {
            if (file) {
                setFormData(prev => ({
                    ...prev,
                    profilePic: file
                }));
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreviewUrl(reader.result);
                };
                reader.readAsDataURL(file);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                profilePic: null
            }));
        }
    };

    // Add this function to load rooms for a branch
    const loadRooms = async (branchId) => {
        if (!branchId) return;

        setLoadingRooms(true);
        setRoomsError(null);

        try {
            const response = await getAllRoomsByBranchId(branchId);

            const data = response.data;

            if (!Array.isArray(data)) {
                throw new Error('Invalid rooms response: expected an array');
            }

            setRooms(data);
        } catch (error) {
            console.error('Error loading rooms:', error);
            setRoomsError(error.message || 'Failed to load rooms');
            setRooms([]);
        } finally {
            setLoadingRooms(false);
        }
    };

    function clearData() {
        setFormData(initialState);
        setPreviewUrl(null);
        setPreviewImages([]);
        setDeletedExistingImages([]);
        setError(null);
        setErrors({});
    }

    const validateImage = (e) => {
        const file = e.target.files?.[0];

        if (!file) {
            setError('No file selected');
            setPreviewUrl(null);
            return false;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image size should be less than 5MB');
            setPreviewUrl(null);
            e.target.value = '';
            return false;
        }

        if (!file.type.startsWith('image/')) {
            setError('Please upload a valid image file');
            setPreviewUrl(null);
            e.target.value = '';
            return false;
        }

        setError('');
        return true;
    };


    // Validation function
    const validateForm = () => {
        const newErrors = {};

        // Required field validations
        const firstNameError = validateName(formData.firstName, 'First name');
        if (firstNameError) newErrors.firstName = firstNameError;

        const lastNameError = validateName(formData.lastName, 'Last name');
        if (lastNameError) newErrors.lastName = lastNameError;

        // Date of birth validation (any age for children)
        if (formData.dob) {
            const ageError = validateAge(formData.dob, 0); // No minimum age for children
            if (ageError) newErrors.dob = ageError;
        } else {
            newErrors.dob = 'Date of birth is required';
        }

        // Profile picture validation (required)
        if (!formData.profilePic && !previewUrl) {
            newErrors.profilePic = 'Profile picture is required';
        }

        // Gender validation (required)
        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
        }

        // Branch ID validation
        const branchIdToSend = formData.branchId || guardian?.branchId || guardian?.branch?.id;
        if (!branchIdToSend || branchIdToSend === 'undefined' || branchIdToSend === 'null') {
            newErrors.branchId = 'Branch is required';
        }

        // Additional images validation (optional but validate if provided)
        if (formData.additionalImages.length > 0) {
            formData.additionalImages.forEach((file, index) => {
                // Only validate if it's a File object (new uploads), not existing image objects
                if (file instanceof File) {
                    const imageError = validateImageFile(file);
                    if (imageError) {
                        newErrors[`additionalImage_${index}`] = imageError;
                    }
                }
            });
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        // Clear previous errors
        setErrors({});
        setError(null);

        // Validate form
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showFormErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            console.log('Form data being sent:', formData);
            console.log('Profile pic type:', typeof formData.profilePic);
            console.log('Profile pic:', formData.profilePic);
            console.log('BranchId from formData:', formData.branchId);
            console.log('Guardian branchId:', guardian?.branchId);
            console.log('Guardian branch.id:', guardian?.branch?.id);
            
            formDataToSend.append('childId', formData.childId);
            formDataToSend.append("firstName", formData.firstName);
            formDataToSend.append("lastName", formData.lastName);
            formDataToSend.append("dob", formData.dob);
            formDataToSend.append("gender", formData.gender);
            formDataToSend.append("status", formData.status);
            formDataToSend.append("remarks", formData.remarks);
            formDataToSend.append("branchId", formData.branchId || guardian?.branchId || guardian?.branch?.id);
            formDataToSend.append("roomId", formData.roomId || '');

            if (formData.profilePic) {
                console.log('Adding profile pic to FormData:', formData.profilePic);
                console.log('Profile pic instanceof File:', formData.profilePic instanceof File);
                formDataToSend.append("profilePic", formData.profilePic);
            }

            if (formData.additionalImages && formData.additionalImages.length > 0) {
                console.log('Processing additionalImages for submission:', formData.additionalImages.length);
                console.log('AdditionalImages details:', formData.additionalImages.map((img, idx) => ({
                    index: idx,
                    name: img.name,
                    size: img.size,
                    type: img.type,
                    isFile: img instanceof File
                })));

                formData.additionalImages.forEach((image, index) => {
                    // Only send File objects (new uploads), not existing image objects
                    if (image instanceof File) {
                        formDataToSend.append("additionalImages", image);
                        console.log(`Added image ${index} to FormData:`, image.name, image.size, 'bytes');
                    } else {
                        console.log(`Skipping image ${index} - not a File object:`, typeof image, image);
                    }
                });
            } else {
                console.log('No additionalImages to process');
            }

            if (isEdit && deletedExistingImages.length > 0) {
                const imageIdsToDelete = deletedExistingImages.map(img => img.imageId);
                console.log('Images to delete:', imageIdsToDelete);
                console.log('Deleted existing images details:', deletedExistingImages);
                imageIdsToDelete.forEach(imageId => {
                    formDataToSend.append("imagesToDelete", imageId.toString());
                    console.log('Added imageToDelete to FormData:', imageId);
                });
            } else {
                console.log('No images to delete - deletedExistingImages length:', deletedExistingImages.length);
            }

            // Debug: Log FormData contents
            console.log('FormData contents before submission:');
            for (let [key, value] of formDataToSend.entries()) {
                if (value instanceof File) {
                    console.log(`${key}: File(${value.name}, ${value.size} bytes)`);
                } else {
                    console.log(`${key}: ${value}`);
                }
            }

            // Submit form
            await onSubmit(formDataToSend);

            // Show success message
            if (isEdit) {
                showUpdateSuccess('Child');
            } else {
                showCreateSuccess('Child');
            }

            // Call onUpdate callback if provided (for refreshing parent component data)
            if (onUpdate && typeof onUpdate === 'function') {
                console.log('Calling onUpdate callback to refresh parent data');
                onUpdate();
            }

            clearData();
            onClose();
        } catch (error) {
            handleApiError(error, isEdit ? "Failed to update child" : "Failed to create child");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        clearData();
        onClose();
    };

    const handleFileChange = (field, files) => {
        if (field === "profilePic") {
            setFormData(prev => ({
                ...prev,
                profilePic: files[0]
            }));
        } else if (field === "additionalImages") {
            const newImages = Array.from(files);
            console.log('Adding new images:', newImages.length, 'files');

            // Filter out duplicates first
            const uniqueNewImages = newImages.filter(newFile =>
                !formData.additionalImages.some(existingFile =>
                    existingFile.name === newFile.name &&
                    existingFile.size === newFile.size &&
                    existingFile.lastModified === newFile.lastModified
                )
            );

            console.log('Filtered unique new images:', uniqueNewImages.length);

            setFormData(prev => {
                const updated = {
                    ...prev,
                    additionalImages: [...prev.additionalImages, ...uniqueNewImages]
                };
                console.log('Updated additionalImages count:', updated.additionalImages.length);
                return updated;
            });

            // Create preview URLs for unique new images (matching what was added to formData)
            const newPreviews = uniqueNewImages.map((file, index) => ({
                id: `new-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
                url: URL.createObjectURL(file),
                file: file,
                isExisting: false
            }));
            console.log('Created new previews for unique images:', newPreviews.length);
            setPreviewImages(prev => {
                const updated = [...prev, ...newPreviews];
                console.log('Total preview images:', updated.length);
                return updated;
            });
        }
    };

    const removeImage = (imageId) => {
        console.log('Removing image with ID:', imageId);
        const imageToRemove = previewImages.find(img => img.id === imageId);
        console.log('Image to remove:', imageToRemove);
        console.log('Current deletedExistingImages:', deletedExistingImages);
        console.log('Current additionalImages count:', formData.additionalImages.length);

        setPreviewImages(prev => {
            const filtered = prev.filter(img => img.id !== imageId);
            console.log('Preview images after removal:', filtered.length);
            return filtered;
        });

        if (imageToRemove && imageToRemove.isExisting) {
            // This is an existing image, mark it for deletion
            console.log('Marking existing image for deletion - originalImage:', imageToRemove.originalImage);
            setDeletedExistingImages(prev => {
                const updated = [...prev, imageToRemove.originalImage];
                console.log('Updated deletedExistingImages:', updated);
                return updated;
            });
        } else if (imageToRemove && imageToRemove.file) {
            // This is a new image, remove it from additionalImages
            console.log('Removing new image from additionalImages - file:', imageToRemove.file);
            setFormData(prev => {
                const filtered = prev.additionalImages.filter(file => file !== imageToRemove.file);
                console.log('AdditionalImages after removal:', filtered.length);
                return {
                    ...prev,
                    additionalImages: filtered
                };
            });
        } else {
            console.log('Image to remove not found or invalid:', imageToRemove);
        }
    };


    // Prevent rendering if there are critical errors
    if (!open) {
        return null;
    }

    return (
        <>
            <Dialog
                key={`child-dialog-${isEdit ? 'edit' : 'add'}-${open ? 'open' : 'closed'}`}
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                keepMounted={false}
            >
                <DialogTitle>{isEdit ? "Edit Child" : "Add New Child"}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                                <Box sx={{position: 'relative'}}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{display: 'block', textAlign: 'center', mb: 1}}
                                    >
                                        Profile Picture *
                                    </SoftTypography>
                                    <Avatar
                                        src={previewUrl}
                                        sx={{width: 100, height: 100, mb: 1}}
                                    />
                                    <input
                                        accept="image/*"
                                        style={{display: 'none'}}
                                        id="profile-pic-upload"
                                        type="file"
                                        onChange={handleImageChange}
                                    />
                                    <label htmlFor="profile-pic-upload">
                                        <IconButton
                                            color="primary"
                                            component="span"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                backgroundColor: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'grey.100'
                                                }
                                            }}
                                        >
                                            <PhotoCamera/>
                                        </IconButton>
                                    </label>
                                    {errors.profilePic && (
                                        <SoftTypography
                                            variant="caption"
                                            color="error"
                                            sx={{display: 'block', textAlign: 'center', mt: 1}}
                                        >
                                            {errors.profilePic}
                                        </SoftTypography>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={0}>
                                    <SoftBox mb={1} ml={0.5}>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                        >
                                            First Name *
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftInput
                                        name="firstName"
                                        type="text"
                                        placeholder="Enter first name"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        error={!!errors.firstName}
                                        required
                                    />
                                    {errors.firstName && (
                                        <SoftTypography
                                            variant="caption"
                                            color="error"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            {errors.firstName}
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Last Name *
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                    name="lastName"
                                    type="text"
                                    placeholder="Enter last name"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    error={!!errors.lastName}
                                    required
                                />
                                {errors.lastName && (
                                    <SoftTypography
                                        variant="caption"
                                        color="error"
                                        sx={{mt: 0.5, ml: 1}}
                                    >
                                        {errors.lastName}
                                    </SoftTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Date of Birth *
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                    name="dob"
                                    type="date"
                                    placeholder="yyyy-mm-dd"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    error={!!errors.dob}
                                    required
                                />
                                {errors.dob && (
                                    <SoftTypography
                                        variant="caption"
                                        color="error"
                                        sx={{mt: 0.5, ml: 1}}
                                    >
                                        {errors.dob}
                                    </SoftTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Gender
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="gender"
                                    value={formData.gender || ''}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d0d7de',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select gender</em>
                                    </MenuItem>
                                    {GENDER_SELECT_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Status
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="status"
                                    value={formData.status || ''}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d0d7de',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select status</em>
                                    </MenuItem>
                                    {CHILD_STATUS_SELECT_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            {/* Branch Selection */}
                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Branch *
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="branchId"
                                    value={formData.branchId || ''}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                    disabled={loadingBranches}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.branchId ? '#d32f2f' : '#d0d7de',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.branchId ? '#d32f2f' : '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.branchId ? '#d32f2f' : '#1976d2',
                                        },
                                    }}
                                >
                                    {loadingBranches ? (
                                        <MenuItem disabled>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CircularProgress size={16}/>
                                                Loading branches...
                                            </Box>
                                        </MenuItem>
                                    ) : branchesError ? (
                                        <MenuItem disabled>
                                            <Typography color="error">
                                                Error loading branches: {branchesError}
                                            </Typography>
                                        </MenuItem>
                                    ) : branches.length === 0 ? (
                                        <MenuItem disabled>
                                            No branches available
                                        </MenuItem>
                                    ) : (
                                        [
                                            <MenuItem key="default" value="" disabled>
                                                <em>Please select a branch</em>
                                            </MenuItem>,
                                            ...branches.map((branch) => (
                                                <MenuItem key={branch.branchId} value={branch.branchId}>
                                                    {branch.location}
                                                </MenuItem>
                                            ))
                                        ]
                                    )}
                                </Select>
                                {errors.branchId && (
                                    <SoftTypography
                                        variant="caption"
                                        color="error"
                                        sx={{mt: 0.5, ml: 1}}
                                    >
                                        {errors.branchId}
                                    </SoftTypography>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Room
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="roomId"
                                    value={formData.roomId || ''}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                    disabled={loadingRooms}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d0d7de',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                    }}
                                >
                                    {loadingRooms ? (
                                        <MenuItem disabled>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CircularProgress size={16}/>
                                                Loading rooms...
                                            </Box>
                                        </MenuItem>
                                    ) : (
                                        [
                                            <MenuItem key="default" value="">
                                                <em>No room assigned</em>
                                            </MenuItem>,
                                            ...rooms.map((room) => (
                                                <MenuItem key={room.roomId} value={room.roomId}>
                                                    {room.name}
                                                </MenuItem>
                                            ))
                                        ]
                                    )}
                                </Select>
                            </Grid>


                            <Grid item xs={12} sm={12}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Remarks
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                    name="remarks"
                                    type="text"
                                    placeholder="Remarks"
                                    value={formData.remarks}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Additional Photos
                                </Typography>
                                {isEdit && (
                                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                        📸 You can remove existing images and add new ones. Changes will be applied
                                        efficiently.
                                    </Typography>
                                )}
                                <input
                                    accept="image/*"
                                    style={{display: "none"}}
                                    id="additional-images-input"
                                    type="file"
                                    multiple
                                    onChange={(e) => {
                                        handleFileChange("additionalImages", e.target.files);
                                        // Reset the input to allow selecting the same files again
                                        e.target.value = '';
                                    }}
                                />
                                <label htmlFor="additional-images-input">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<AddPhotoAlternateIcon/>}
                                    >
                                        Upload Additional Photos
                                    </Button>
                                </label>

                                {/* Image Previews */}
                                <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                                    {previewImages.length > 0 ? (
                                        previewImages.map((image) => (
                                            <Box
                                                key={image.id}
                                                position="relative"
                                                sx={{
                                                    width: 150,
                                                    height: 150,
                                                    border: "1px solid #ddd",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                }}
                                            >
                                                <img
                                                    src={image.url}
                                                    alt="Preview"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        position: "absolute",
                                                        top: 4,
                                                        right: 4,
                                                        backgroundColor: "rgba(255,255,255,0.9)",
                                                        color: "error.main",
                                                        '&:hover': {
                                                            backgroundColor: "rgba(255,255,255,1)",
                                                        }
                                                    }}
                                                    onClick={() => removeImage(image.id)}
                                                >
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                                {image.isExisting && (
                                                    <Chip
                                                        label="Existing"
                                                        size="small"
                                                        sx={{
                                                            position: "absolute",
                                                            bottom: 4,
                                                            left: 4,
                                                            backgroundColor: "success.main",
                                                            color: "white",
                                                            fontWeight: "bold",
                                                            fontSize: "0.7rem"
                                                        }}
                                                    />
                                                )}

                                            </Box>
                                        ))
                                    ) : (
                                        <Box sx={{
                                            width: '100%',
                                            p: 3,
                                            textAlign: 'center',
                                            backgroundColor: 'rgba(0,0,0,0.05)',
                                            borderRadius: 2,
                                            border: '2px dashed #ddd'
                                        }}>
                                            <AddPhotoAlternateIcon sx={{fontSize: 48, color: 'text.secondary', mb: 1}}/>
                                            <Typography variant="body2" color="text.secondary">
                                                No additional photos uploaded yet
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                        {error && (
                            <div style={{color: "red", marginTop: "10px"}}>
                                {error}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                        <SoftButton type="submit" variant="contained" color="primary"
                                    disabled={isSubmitting || !!error}>
                            {isSubmitting ? "Saving…" : (isEdit ? "Update Child" : "Add Child")}
                        </SoftButton>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}

AddChildrenDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    child: PropTypes.object,
    guardian: PropTypes.object,
    isEdit: PropTypes.bool,
    preSelectedGuardian: PropTypes.object,
    disableGuardianSelection: PropTypes.bool,
    fromGuardianView: PropTypes.bool,
    onUpdate: PropTypes.func
};

export default AddChildrenDialog;
